import React from "react";
import { InfoPopup } from "../InfoPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Chip } from "../Chip";
import "./InputTags.css";

interface InputTagsProps {
  id: string;
  className?: string;
  onChange: (tags: string[]) => void;
  initialValue?: string[];
  onError?: (error: string) => void;
  // When set, the field shows an always-visible hint stating the allowed range
  // and turns the hint into an explicit reason while the count is below the
  // minimum. maxTags also caps how many tags the field accepts (default 7).
  minTags?: number;
  maxTags?: number;
}

export const InputTags: React.FC<InputTagsProps> = (props) => {
  const [tags, setTags] = React.useState<string[]>(props.initialValue || []);
  const maxTags = props.maxTags ?? 7;
  const minTags = props.minTags;
  const belowMin = minTags !== undefined && tags.length < minTags;

  function insertTag(value: string): boolean {
    if (tags.length >= maxTags) {
      props.onError?.(`Limite de ${maxTags} tags atingido`);
      return false;
    }

    const tag = value.trim().normalize("NFC");
    const length = Array.from(tag).length;
    const tableError: { [key: string]: boolean } = {
      "Tag já existe": tags.some((existing) => existing.normalize("NFC") === tag),
      "Tag está vazia": length === 0,
      "Tag muito curta": length < 2,
      "Tag muito longa": length > 50,
      "Tag contém caracteres inválidos":
        /[\p{Cc}\p{Zl}\p{Zp}]/u.test(value) ||
        !/^\p{L}[\p{L}\p{M}]*(?:-\p{L}[\p{L}\p{M}]*)*$/u.test(tag),
    };

    const firstError = Object.entries(tableError).find(([_, isError]) => isError);
    if (firstError) {
      props.onError?.(firstError[0]);
      return false;
    }

    const newTags = [...tags, tag];
    setTags(newTags);
    props.onChange(newTags);
    return true;
  }

  function removeTag(index: number) {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    props.onChange(newTags);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // Dead keys and IME composition belong to the browser. Validate the
    // completed value rather than cancelling accent keys, navigation or paste.
    if (
      event.nativeEvent.isComposing ||
      event.keyCode === 229 ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    )
      return;
    const goKeys = ["Enter", " "];
    const inputValue = event.currentTarget.value;

    if (goKeys.includes(event.key)) {
      event.preventDefault();
      if (insertTag(inputValue)) event.currentTarget.value = "";
      return;
    }
  }

  return (
    <div id={props.id} className={`inputTagsContainer ${props.className || ""}`}>
      <div className="inputTagsFieldContainer">
        <input
          className="inputTagsField"
          onKeyDown={handleKeyDown}
          placeholder="Adicione tags"
          disabled={tags.length >= maxTags}
        />
        <InfoPopup
          position="nw"
          id={`${props.id}-infoPopup`}
          classNameBox="inputTagsInfoPopupContainer"
          triggerElement={
            <div className="inputTagsInfoIcon">
              <FontAwesomeIcon icon={faCircleInfo} />
            </div>
          }
        >
          <span className="inputTagsInfoPopup">
            As tags são fundamentais para categorizar e facilitar a busca por anúncios. Utilize
            palavras-chave relevantes que descrevam o conteúdo do anúncio. Separe cada tag
            pressionando Enter ou Espaço.
            {minTags !== undefined
              ? ` Adicione de ${minTags} a ${maxTags} tags.`
              : ` Adicione até ${maxTags} tags.`}
          </span>
        </InfoPopup>
      </div>
      {tags.length > 0 && (
        <div className="inputTagsResultContainer">
          {tags.map((tag, index) => (
            <Chip class="inputTagsChip" k={String(index)}>
              <span className="tagName">{tag}</span>
              <FontAwesomeIcon
                className="inputTagsChipIconDelete"
                icon={faTimes}
                onClick={() => removeTag(index)}
              />
            </Chip>
          ))}
        </div>
      )}
      <div className={`inputTagsHint ${belowMin ? "inputTagsHintWarning" : ""}`} aria-live="polite">
        <span className="inputTagsHintText">
          {minTags !== undefined
            ? belowMin
              ? `Adicione no mínimo ${minTags} tags`
              : `Adicione de ${minTags} a ${maxTags} tags`
            : `Adicione até ${maxTags} tags`}
        </span>
        <span className="inputTagsHintCounter">{`${tags.length}/${maxTags}`}</span>
      </div>
    </div>
  );
};
